<template>
    <div v-if="loaded">
        <rkm-area
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :max-date="maxDate"
                            :min-date="minDate"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.unidades"
                            @change="handleUnidadeChange"
                            default-option-text="Todas"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!model.unidadeId"
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Profissional"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-input-autocomplete
                            :disabled="!hasPeriodo"
                            :repository="repositories.relatorioTempoAtendimento.paciente"
                            :request-query-string-params="pacienteSearchParams"
                            :search-fields="pacienteSearchFields"
                            label="Paciente"
                            placeholder="Nome, CNS, CPF ou Prontuário"
                            text-field="nome"
                            v-model="model.paciente"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.classificacoesRisco"
                            @change="handleClassificacaoRiscoChange"
                            default-option-text="Todas"
                            label="Classificação de risco"
                            show-default-option
                            text-field="nome"
                            v-model="model.classificacaoRiscoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col>
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.cids"
                            @change="handleCidChange"
                            default-option-text="Todos"
                            label="CID"
                            show-default-option
                            text-field="descricao"
                            v-model="model.cidId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :disabled="!hasPeriodo"
                            :options="form.ocorrencias"
                            @change="handleOcorrenciaChange"
                            default-option-text="Todas"
                            label="Ocorrência"
                            show-default-option
                            v-model="model.ocorrencia"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="clear"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area title="Resultado">
            <template v-slot:trailing>
                <rkm-button
                    :disabled="atendimentos.length === 0"
                    @click="print"
                    color="info"
                    small
                    title="Imprimir"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    :disabled="atendimentos.length === 0"
                    @click="exportTo"
                    color="info"
                    small
                    title="Exportar"
                >
                    <rkm-icon icon="file-export" />
                </rkm-button>
            </template>

            <rkm-table
                :fields="atendimentoFields"
                :items="atendimentos"
                :sort-by="sortBy"
                :sort-desc="sortDesc"
                @sort-changed="onSortChanged"
            >
                <template v-slot:cell(actions)="{ item }">
                    <rkm-button
                        @click="$refs.ModalDetalhe.open(prepareData(), item)"
                        color="primary"
                        small
                        title="Exibir detalhes"
                    >
                        <rkm-icon icon="search" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <rkm-area
            v-if="totalizadores"
            title="Totalizadores"
            type="fieldset"
        >
            <rkm-row>
                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Atendimentos"
                        v-model="totalizadores.totalAtendimentos"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Pacientes"
                        v-model="totalizadores.totalPacientes"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Média de espera"
                        tooltip-top="Somatório do tempo de espera / quantidade de atendimentos"
                        v-model="totalizadores.mediaEspera"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Média de atendimento"
                        tooltip-top="Somatório do tempo de atendimento / quantidade de atendimentos"
                        v-model="totalizadores.mediaAtendimento"
                    />
                </rkm-col>

                <rkm-col>
                    <rkm-form-input
                        disabled
                        label="Média de permanência"
                        tooltip-top="Somatório do tempo total / quantidade de atendimentos"
                        v-model="totalizadores.mediaPermanencia"
                    />
                </rkm-col>
            </rkm-row>
        </rkm-area>

        <ModalDetalhe ref="ModalDetalhe" />
    </div>
</template>

<script>
// Components.
import ModalDetalhe from './components/detalhe/Modal'

// Mixins.
import { repository, error, stringCase, file, formats } from '@/mixins'

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

export default {
    components: {
        ModalDetalhe,
    },
    computed: {
        validations() {
            return {
                periodo: { required: true },
            }
        },
        minDate() {
            if (this.model.periodo.end === null) {
                return null
            }

            const min = this.others.moment(this.model.periodo.end).subtract(1, 'month')

            return min.format('DD/MM/YYYY')
        },
        maxDate() {
            const dataAtual = this.others.moment(this.additionals.dataAtual, 'DD/MM/YYYY')

            if (this.model.periodo.start === null) {
                return dataAtual.format('DD/MM/YYYY')
            }

            const max = this.others.moment(this.model.periodo.start).add(1, 'month')

            if (max > dataAtual) {
                return dataAtual.format('DD/MM/YYYY')
            }

            return max.format('DD/MM/YYYY')
        },
        pacienteSearchParams() {
            return {
                periodo: this.hasPeriodo ? this.getPeriodo() : null,
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
            }
        },
        hasPeriodo() {
            return this.model.periodo.start !== null && this.model.periodo.end !== null
        },
        pacienteSearchFields() {
            return [
                { key: 'cidadao.nome', searchable: true },
                { key: 'cidadao.cns', searchable: true },
                { key: 'cidadao.cpf', searchable: true },
                { key: 'paciente.prontuario', searchable: true },
            ]
        },
        atendimentoFields() {
            return [
                { key: 'dataAberturaFaa', label: 'Data', formatter: v => this.formatDate(v), sortable: true },
                { key: 'pacienteNome', label: 'Paciente', sortable: true },
                { key: 'pacienteProntuario', label: 'Prontuário', sortable: true },
                { key: 'faaNumero', label: 'FAA', sortable: true },
                { key: 'classificacaoRiscoNome', label: 'Classificação de risco', sortable: true },
                { key: 'cids', label: 'CID(s)', formatter: v => v.join(', ') },
                { key: 'tempoTotalEspera', label: 'Tempo espera', class: 'col-min text-center', sortable: true },
                { key: 'tempoTotalAtendimento', label: 'Tempo atendimento', class: 'col-min text-center', sortable: true },
                { key: 'tempoTotal', label: 'Tempo total', class: 'col-min text-center', sortable: true },
                { key: 'actions', label: 'Ações', class: 'col-min text-center' },
            ]
        },
    },
    created() {
        this.repositories = {
            relatorioTempoAtendimento: this.getConectaSusRepository('prontoAtendimento.relatorioTempoAtendimento'),
            impressao: this.getConectaSusRepository('impressao.prontoAtendimento'),
            exportacao: this.getConectaSusRepository('exportacao.prontoAtendimento'),
        }

        this.load()
    },
    data: () => ({
        loaded: false,
        additionals: null,
        form: null,
        model: null,
        atendimentos: [],
        totalizadores: null,
        sortBy: 'dataAberturaFaa',
        sortDesc: false,
        unwatchers: {
            unwatchPeriodo: null,
            unwatchPaciente: null,
        },
        others: { moment }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioTempoAtendimento.create()
                .then(({ data }) => {
                    this.additionals = data.additionals
                    this.form = data.form
                    this.model = data.model

                    this.addWatchers()

                    this.loaded = true
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {
            this.unwatchers.unwatchPeriodo = this.$watch('model.periodo', {
                deep: true,
                handler: this.handlePeriodoChange
            })

            this.unwatchers.unwatchPaciente = this.$watch('model.paciente', () => this.handlePacienteChange())
        },
        handlePeriodoChange(periodo) {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()

            if (periodo.start === null || periodo.end === null) return

            this.loadFromUnidade()
        },
        handleUnidadeChange() {
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromInfraestrutura()
        },
        handleInfraestruturaChange() {
            this.form.usuarios = []
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.usuarioId = null
            this.model.paciente = null
            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromUsuario()
        },
        handleUsuarioChange() {
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.paciente = null
            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromClassificacaoRisco()
        },
        handlePacienteChange() {
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromClassificacaoRisco()
        },
        handleClassificacaoRiscoChange() {
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.cidId = null
            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromCid()
        },
        handleCidChange() {
            this.form.ocorrencias = []

            this.model.ocorrencia = null

            this.clearResult()
            this.loadFromOcorrencia()
        },
        handleOcorrenciaChange() {
            this.clearResult()
        },
        loadFromUnidade() {
            const params = {
                loadFrom: 'unidades',
                periodo: this.getPeriodo(),
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.unidades = data.unidades
                    this.form.infraestruturas = data.infraestruturas
                    this.form.usuarios = data.usuarios
                    this.form.classificacoesRisco = data.classificacoesRisco
                    this.form.cids = data.cids
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromInfraestrutura() {
            const params = {
                loadFrom: 'infraestruturas',
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.infraestruturas = data.infraestruturas
                    this.form.usuarios = data.usuarios
                    this.form.classificacoesRisco = data.classificacoesRisco
                    this.form.cids = data.cids
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromUsuario() {
            const params = {
                loadFrom: 'usuarios',
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.usuarios = data.usuarios
                    this.form.classificacoesRisco = data.classificacoesRisco
                    this.form.cids = data.cids
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromClassificacaoRisco() {
            const params = {
                loadFrom: 'classificacoes-risco',
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente?.id ?? null
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.classificacoesRisco = data.classificacoesRisco
                    this.form.cids = data.cids
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromCid() {
            const params = {
                loadFrom: 'cids',
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente?.id ?? null,
                classificacaoRiscoId: this.model.classificacaoRiscoId,
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.cids = data.cids
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        loadFromOcorrencia() {
            const params = {
                loadFrom: 'ocorrencias',
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente?.id ?? null,
                classificacaoRiscoId: this.model.classificacaoRiscoId,
                cidId: this.model.cidId,
            }

            this.setLoading()

            this.repositories.relatorioTempoAtendimento.list(this.camelObjectKeysToSnakeRecursive(params))
                .then(({ data }) => {
                    this.form.ocorrencias = data.ocorrencias
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            this.setLoading()

            this.repositories.relatorioTempoAtendimento.index(this.prepareData())
                .then(({ data }) => {
                    this.atendimentos = data.atendimentos
                    this.totalizadores = data.totalizadores
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        clear() {
            this.removeWatchers()

            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.classificacoesRisco = []
            this.form.cids = []
            this.form.ocorrencias = []

            this.model.periodo = { start: null, end: null }
            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.paciente = null
            this.model.classificacaoRiscoId = null
            this.model.cidId = null
            this.model.ocorrencia = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
            this.addWatchers()
            this.clearResult()
        },
        removeWatchers() {
            this.unwatchers.unwatchPeriodo()
            this.unwatchers.unwatchPaciente()
        },
        clearResult() {
            this.atendimentos = []
            this.totalizadores = null
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
        print() {
            this.setLoading()

            this.repositories.impressao.relatorioTempoAtendimento(this.prepareData(true))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            this.repositories.exportacao.exportRelatorioTempoAtendimento(this.prepareData(true))
                .then(({ data, headers }) => this.openFile(data, headers['content-type']))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        prepareData(isPrintExport = false) {
            const params = {
                periodo: this.getPeriodo(),
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                pacienteId: this.model.paciente?.id ?? null,
                classificacaoRiscoId: this.model.classificacaoRiscoId,
                cidId: this.model.cidId,
                ocorrencia: this.model.ocorrencia,
            }

            if (isPrintExport) {
                params.sortBy = this.sortBy ? this.camelToSnake(this.sortBy) : null
                params.sortDesc = this.sortDesc

                params.columns = this.atendimentoFields.filter(f => f.key !== 'actions').map(f => ({
                    key: this.camelToSnake(f.key),
                    label: f.label,
                    class: f.class
                }))
            }

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        getPeriodo() {
            return {
                start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
            }
        },
    },
    mixins: [repository, error, stringCase, file, formats],
}
</script>